import { useEffect, useState } from 'react';

import { SaveableField } from '@/components/ui/SaveableField';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

export function useChatPromptFields() {
  const user = userStore.user;

  const [inputGlobalPrompt, setInputGlobalPrompt] = useState(
    user?.global_prompt ?? ''
  );

  useEffect(() => {
    userStore.refresh().then(() => {
      setInputGlobalPrompt(userStore.user?.global_prompt ?? '');
    });
  }, []);

  const globalPromptDirty = inputGlobalPrompt !== (user?.global_prompt ?? '');

  return {
    inputGlobalPrompt,
    setInputGlobalPrompt,
    globalPromptDirty,
    saveGlobalPrompt: async (): Promise<boolean> => {
      if (!user || !globalPromptDirty) return false;

      try {
        await userStore.syncUpdate(
          { global_prompt: inputGlobalPrompt },
          { throwOnError: true }
        );
        trackEvent('CHAT_SETTING_PROMPT_UPDATED');
        return true;
      } catch {
        return false;
      }
    },
    discardGlobalPrompt: () => {
      setInputGlobalPrompt(userStore.user?.global_prompt ?? '');
    },
  };
}

export type ChatPromptFields = ReturnType<typeof useChatPromptFields>;

type PromptTextAreaProps = {
  className?: string;
  wrapperClassName?: string;
  fields: ChatPromptFields;
};

export const CustomInstructionsTextArea = ({
  className,
  wrapperClassName,
  fields,
}: PromptTextAreaProps) => {
  return (
    <SaveableField
      id="custom-prompt"
      className={className}
      wrapperClassName={wrapperClassName}
      placeholder="Enter your custom instructions"
      value={fields.inputGlobalPrompt}
      dirty={fields.globalPromptDirty}
      onChange={fields.setInputGlobalPrompt}
      onSave={fields.saveGlobalPrompt}
      onDiscard={fields.discardGlobalPrompt}
      enableVoiceInput
    />
  );
};
